/* Copyright (c) 2020-present CloudIO Inc */

import { checkForFixedChild } from 'componentregistry/utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { paperVariant } from '../../registry/constants';
import { defaultValue } from './FloatingPanel';
import { helpRenders } from './FloatingPanel_Help';

const name = 'FloatingPanel';

const FloatingPanelManager: ComponentManagerFn<'FloatingPanel'> = (
  platform: Platform,
): ComponentManager<'FloatingPanel'> => {
  return {
    ...baseComponentManager<'FloatingPanel'>(platform, 'FloatingPanel'),
    defaultValue,
    defaultChildren: ['FlexPanel'],
    isChildAllowed: (page, item, childType, context) =>
      (
        [
          'BoxContainer',
          'Card',
          'DataStoreReference',
          'FlexPanel',
          'RowPanel',
          'TabPanel',
        ] as ItemType[]
      ).includes(childType) &&
      checkForFixedChild(platform, page, item, childType, name, context),
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'elevation':
          return itemData.variant === 'elevation';
        case 'borderRadius':
          return !itemData.square;
        case 'dragMomentum':
          return itemData.draggable;
        default:
          return true;
      }
    },
    props: {
      variant: {
        label: 'Variant',
        type: 'select',
        options: paperVariant,
        helpText: 'The variant to use.',
      },
      elevation: {
        label: 'Elevation',
        type: 'number',
        helpText: 'Shadow depth. It accepts values between 0 and 24 inclusive.',
        max: 24,
      },
      square: {
        label: 'Square',
        type: 'boolean',
        helpText: 'If checked, rounded corners are disabled.',
      },
      borderRadius: {
        label: 'Border Radius',
        type: 'number',
        max: 12,
      },
      padding: {
        label: 'Padding',
        type: 'padding',
        helpText: 'Defines the number of grids the component is going to use.',
        category: 'Spacing',
      },
      border: {
        label: 'Border',
        type: 'border',
        helpText: 'Defines the border.',
        category: 'Spacing',
      },
      top: {
        label: 'Top',
        type: 'numberInput',
        category: 'Position',
        helpText: 'Use -1 to not set this position',
      },
      right: {
        label: 'Right',
        type: 'numberInput',
        category: 'Position',
        helpText: 'Use -1 to not set this position',
      },
      bottom: {
        label: 'Bottom',
        type: 'numberInput',
        category: 'Position',
        helpText: 'Use -1 to not set this position',
      },
      left: {
        label: 'Left',
        type: 'numberInput',
        category: 'Position',
        helpText: 'Use -1 to not set this position',
      },
      draggable: {
        label: 'Draggable',
        type: 'boolean',
        category: 'Position',
      },
      dragMomentum: {
        label: 'Drag Momentum',
        type: 'boolean',
        category: 'Position',
      },
      panelHeight: {
        label: 'Height',
        type: 'numberInput',
        category: 'Size',
      },
      panelWidth: {
        label: 'Width',
        type: 'numberInput',
        category: 'Size',
      },
    },
    category: 'Containers',
    name,
    canHaveChildren: () => true,
    layout: 'fixed',
    icon: { iconName: 'window-restore' },
    description: `**Overview**<br/>
The Floating Panel is a versatile container designed to sit above the standard page layout. Unlike standard components that stack vertically or horizontally, the Floating Panel uses absolute positioning, allowing you to place it anywhere on the screen regardless of the underlying content.
This component is ideal for building overlay interfaces such as "Quick Action" toolbars, persistent widgets, or custom dialogs that users can keep open while interacting with the main page.

**1. Absolute Positioning**<br/>
The core feature of this component is its ability to be pinned to specific coordinates on the screen.
* **Top / Right / Bottom / Left:** These properties define the exact distance (in pixels or grid units) from the respective edge of the screen.
* **How to use:** To pin a panel to the bottom-right corner, you might set Bottom to 20 and Right to 20, while leaving Top and Left as -1.
* **Note:** Use a value of -1 to ignore a specific side. For example, if Top is -1, the vertical position will be determined solely by the Bottom value.

**2. Interactive Mobility**<br/>
You can transform a static overlay into a dynamic tool by enabling user interaction.
* **Draggable:** When enabled, users can click and hold the panel to move it freely around the screen. This is essential for "Tool Palette" designs where the user might need to move the panel out of the way to see data underneath.
* **Drag Momentum:** Adds physics to the drag action. If the user "throws" the panel with a quick mouse movement, it will continue to slide briefly before coming to a stop, creating a fluid, native-app feel. (Visible only when Draggable is enabled).

**3. Visual Appearance (Paper Styles)**<br/>
The Floating Panel offers extensive styling options to ensure it stands out visually from the content behind it.
* **Variant:** Selects the visual theme (e.g., Elevation).
* **Elevation:** Controls the depth of the drop shadow (0-24). Higher numbers create a larger, softer shadow, making the panel appear to float higher above the page. (Visible only when Variant is 'elevation').
* **Square:** Disables rounded corners, forcing the panel to be perfectly rectangular.
* **Border Radius:** Fine-tunes the roundness of the corners. (Visible only when Square is disabled).
* **Border:** Adds a defined outline to the panel, useful for high-contrast modes or flat designs.

**4. Size & Content**<br/>
* **Height & Width:** Explicit dimensions for the panel. Since it is absolutely positioned, it does not naturally expand to fill a parent container; you must define its size or rely on the content inside to push it open.
* **Padding:** Defines the internal spacing between the panel edges and its children.`,
    helpRenders,
  };
};

export default FloatingPanelManager;
